<template>
  <div>
    <!-- Header -->
    <section class="page-header">
      <div class="premium-container">
        <div class="fade-in">
          <span class="premium-label">Our services</span>
          <h1 class="premium-heading-lg">
            Websites. Automation.<br>AI. All under one roof.
          </h1>
          <p class="premium-subheading">
            From custom websites to AI-powered automation,
            we deliver the digital tools your business needs to grow.
          </p>
        </div>
      </div>
    </section>

    <!-- Core Services -->
    <section class="premium-section-alt">
      <div class="premium-container">
        <div class="services-list">
          <router-link
            v-for="(service, index) in coreServices"
            :key="service.slug"
            :to="'/services/' + service.slug"
            class="service-row fade-in"
            :class="'fade-in-delay-' + ((index % 4) + 1)"
          >
            <div class="service-row-left">
              <span class="service-num">{{ String(index + 1).padStart(2, '0') }}</span>
              <h3 class="service-row-title">{{ service.title }}</h3>
            </div>
            <p class="service-row-desc premium-body">{{ service.description }}</p>
            <div class="service-row-arrow">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="none"><path d="M6 3L11 8L6 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </div>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Capabilities -->
    <section class="premium-section">
      <div class="premium-container">
        <div class="section-header text-center fade-in">
          <span class="premium-label">Capabilities</span>
          <h2 class="premium-heading" style="max-width: 600px; margin-left: auto; margin-right: auto;">
            The skills behind every project.
          </h2>
        </div>

        <div class="capabilities-grid">
          <div
            v-for="(cap, index) in capabilities"
            :key="cap.label"
            class="cap-item fade-in"
            :class="'fade-in-delay-' + ((index % 4) + 1)"
          >
            <div class="cap-icon" v-html="cap.icon"></div>
            <span class="cap-label">{{ cap.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Process -->
    <section class="premium-section-alt">
      <div class="premium-container">
        <div class="section-header text-center fade-in">
          <span class="premium-label">Our process</span>
          <h2 class="premium-heading" style="max-width: 560px; margin-left: auto; margin-right: auto;">
            Kickoff to launch in 2–3 weeks.
          </h2>
        </div>

        <div class="process-grid">
          <div
            v-for="(step, index) in process"
            :key="step.title"
            class="process-step fade-in"
            :class="'fade-in-delay-' + (index + 1)"
          >
            <div class="step-number">{{ String(index + 1).padStart(2, '0') }}</div>
            <h3 class="step-title">{{ step.title }}</h3>
            <p class="step-desc premium-body">{{ step.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="premium-section">
      <div class="premium-container">
        <div class="cta-banner fade-in">
          <div class="cta-content">
            <h2 class="cta-title">Need something custom?</h2>
            <p class="cta-desc">Tell us what you need. We'll scope it, quote it, and deliver it fast.</p>
          </div>
          <router-link to="/contact" class="premium-btn premium-btn-dark">
            Get a free quote
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8H13M13 8L8 3M13 8L8 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useSeo, useBreadcrumbSchema } from '@/composables/useSeo'

export default {
  name: 'Services',
  setup() {
    useSeo({
      title: 'Web Development, AI & Automation Services | Digitly',
      description: 'Custom websites, AI automation, e-commerce, and business tools for UK businesses. Fast delivery in 2-3 weeks. From £499.',
      keywords: 'web development services UK, AI automation, custom website design, e-commerce development, booking system, business automation, website maintenance',
      path: '/services'
    })

    useBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Services', path: '/services' }
    ])
  },
  data() {
    return {
      coreServices: [
        {
          slug: 'web-development',
          title: 'Web Development',
          description: 'Custom-built, responsive websites designed and developed for your specific business needs.'
        },
        {
          slug: 'ai-automation',
          title: 'AI & Automation',
          description: 'AI chatbots, automated workflows, smart booking, and intelligent tools that save you hours every week.'
        },
        {
          slug: 'ecommerce',
          title: 'E-Commerce',
          description: 'Online stores with secure payments, inventory management, and a seamless customer experience.'
        },
        {
          slug: 'maintenance',
          title: 'Maintenance & Support',
          description: 'Ongoing security monitoring, performance optimisation, and dedicated technical support.'
        }
      ],
      capabilities: [
        { label: 'Custom Web Apps', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="4" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M8 22H16M12 18V22" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' },
        { label: 'AI Chatbots', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>' },
        { label: 'Booking Systems', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M3 10H21M16 2V6M8 2V6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' },
        { label: 'SEO & Performance', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="1.5"/><path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' },
        { label: 'Payments', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M2 10H22" stroke="currentColor" stroke-width="1.5"/></svg>' },
        { label: 'Analytics', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M3 3V21H21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M7 16L11 11L15 14L21 7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' },
        { label: 'CRM Integration', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M17 21V19C17 16.7909 15.2091 15 13 15H5C2.79086 15 1 16.7909 1 19V21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" stroke-width="1.5"/><path d="M23 21V19C23 17.1362 21.7252 15.5701 20 15.126" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M16 3.12602C17.7252 3.57006 19 5.13616 19 7C19 8.86384 17.7252 10.4299 16 10.874" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' },
        { label: 'Cloud Hosting', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M18 10H16.74C16.3659 8.55 15.5928 7.24169 14.5086 6.2174C13.4245 5.19312 12.0727 4.49562 10.6069 4.20457C9.14107 3.91352 7.62234 4.04073 6.22376 4.57152C4.82518 5.10231 3.60435 6.01438 2.70226 7.20335C1.80017 8.39232 1.25369 9.80888 1.12402 11.2918C0.994353 12.7746 1.28669 14.2634 1.96696 15.5902C2.64722 16.917 3.68725 18.0283 4.96818 18.7948C6.24911 19.5614 7.71964 19.9526 9.21 19.924H18C19.3261 19.924 20.5979 19.3972 21.5355 18.4596C22.4732 17.5219 23 16.2501 23 14.924C23 13.5979 22.4732 12.3261 21.5355 11.3884C20.5979 10.4508 19.3261 9.924 18 9.924V10Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>' },
        { label: 'Email Automation', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="2" y="4" width="20" height="16" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M22 7L13.03 12.7C12.7213 12.8934 12.3643 12.9965 12 12.9965C11.6357 12.9965 11.2787 12.8934 10.97 12.7L2 7" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>' },
        { label: 'API Development', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M16 18L22 12L16 6M8 6L2 12L8 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>' },
        { label: 'Security & SSL', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>' },
        { label: 'Database Design', icon: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none"><ellipse cx="12" cy="5" rx="9" ry="3" stroke="currentColor" stroke-width="1.5"/><path d="M21 12C21 13.6569 16.9706 15 12 15C7.02944 15 3 13.6569 3 12" stroke="currentColor" stroke-width="1.5"/><path d="M3 5V19C3 20.6569 7.02944 22 12 22C16.9706 22 21 20.6569 21 19V5" stroke="currentColor" stroke-width="1.5"/></svg>' },
      ],
      process: [
        {
          title: 'Discovery call',
          description: 'A quick call to understand your business, goals, and what you need built. Free, no strings attached.'
        },
        {
          title: 'Proposal & quote',
          description: 'Within 48 hours you\'ll have a clear scope, timeline, and fixed-price quote.'
        },
        {
          title: 'Build & iterate',
          description: 'We build in short sprints, sharing progress regularly so you\'re never in the dark.'
        },
        {
          title: 'Launch & support',
          description: 'We deploy, test everything, and stay on hand to make sure it runs perfectly.'
        }
      ]
    }
  }
}
</script>

<style scoped>
.page-header {
  padding: 140px 0 80px;
  background: var(--color-bg);
}

.services-list {
  display: flex;
  flex-direction: column;
}

.service-row {
  display: grid;
  grid-template-columns: 1fr 1.5fr auto;
  gap: 32px;
  align-items: center;
  padding: 32px 0;
  border-bottom: 1px solid var(--color-border);
  text-decoration: none;
  transition: padding 0.2s ease;
}

.service-row:first-child {
  border-top: 1px solid var(--color-border);
}

.service-row:hover {
  padding-left: 8px;
}

.service-row-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.service-num {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-text-tertiary);
  letter-spacing: 0.05em;
}

.service-row-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  letter-spacing: -0.02em;
}

.service-row-arrow {
  color: var(--color-text-tertiary);
  transition: transform 0.2s ease, color 0.2s ease;
}

.service-row:hover .service-row-arrow {
  transform: translateX(4px);
  color: var(--color-text);
}

.section-header {
  margin-bottom: 64px;
}

.capabilities-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.cap-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px 16px;
  background: var(--color-bg-alt);
  border: 1px solid var(--color-border);
  border-radius: 12px;
  text-align: center;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
}

.cap-item:hover {
  border-color: #C8C8CC;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.07);
  transform: translateY(-3px);
}

.cap-icon {
  flex-shrink: 0;
  color: var(--color-text-secondary);
  line-height: 0;
}

.cap-label {
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text);
  letter-spacing: -0.01em;
}

.process-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
}

.step-number {
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--color-text-tertiary);
  margin-bottom: 16px;
  letter-spacing: 0.05em;
}

.step-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 8px;
  letter-spacing: -0.01em;
}

.cta-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 56px 64px;
  background: var(--color-bg-alt);
  border-radius: var(--radius-lg);
  border: 1px solid var(--color-border);
}

.cta-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 4px;
  letter-spacing: -0.02em;
}

.cta-desc {
  font-size: 1rem;
  color: var(--color-text-secondary);
}

@media (max-width: 960px) {
  .page-header {
    padding: 56px 0 56px;
  }

  .service-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .service-row-arrow {
    display: none;
  }

  .capabilities-grid {
    grid-template-columns: 1fr 1fr;
  }

  .process-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .cta-banner {
    flex-direction: column;
    gap: 24px;
    padding: 40px 32px;
    text-align: center;
  }
}

@media (max-width: 600px) {
  .page-header {
    padding: 36px 0 48px;
  }

  .capabilities-grid {
    grid-template-columns: 1fr;
  }

  .process-grid {
    grid-template-columns: 1fr;
  }
}
</style>
